import { useState } from 'react';
import type { SpendByArche } from '../../lib/api';
import { formatCreditsWithDollar } from '@/lib/format-credits';
import { ArcheIconImg } from '@/lib/arche-icon-url';

interface SpendByArcheSummaryProps {
  arches: SpendByArche[];
  loading: boolean;
  /** Fetch failed — render an explicit error, never a false "no spending". */
  error?: boolean;
}

/** How many arche rows to show before the "Show all" expander. */
const COLLAPSED_LIMIT = 8;

/** Client-owned label for the `archeId: null` no-arche-context bucket. */
const PLATFORM_BUCKET_LABEL = 'Platform / Other';

/**
 * Per-arche spend breakdown — the sibling of `SpendingSummary` (which groups by
 * category). Each arche is a relative bar with its icon + human-readable name.
 *
 * The server's `archeId: null` row (spend with no arche context: plugin
 * builder, platform utilities, core-system chat) is deliberately NOT rendered
 * as a bar. It is typically far larger than any single arche — on prod it is
 * ~94% of all spend, dominated by builder usage — so including it in the bar
 * scale pins it at 100% and collapses every real arche to the 1% minimum
 * width, making the chart unreadable. It renders as a footer line instead, so
 * the bars scale against each other while the total still reconciles with the
 * by-category card.
 *
 * Purely presentational: the Credits page owns the `useSpendByArche` fetch and
 * passes the rows in, matching how `SpendingSummary` receives its categories.
 */
export function SpendByArcheSummary({ arches, loading, error = false }: SpendByArcheSummaryProps) {
  const [expanded, setExpanded] = useState(false);

  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <div className="animate-pulse">
          <div className="h-5 bg-muted rounded w-40 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Distinct from the empty state: a failed fetch must never read as
  // "you have no spending" (also covers the deploy window where the
  // frontend is live before the API route).
  if (error) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <h2 className="text-sm font-medium text-muted-foreground mb-3">Spending by Arche</h2>
        <p className="text-sm text-muted-foreground">
          Couldn&apos;t load arche spending. Try refreshing.
        </p>
      </div>
    );
  }

  if (arches.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <h2 className="text-sm font-medium text-muted-foreground mb-3">Spending by Arche</h2>
        <p className="text-sm text-muted-foreground">No arche spending yet</p>
      </div>
    );
  }

  const platformRow = arches.find((a) => a.archeId === null) ?? null;
  const archeRows = arches.filter((a) => a.archeId !== null);

  // Grand total spans BOTH the bars and the footer row — it must stay equal to
  // the by-category card's total, which has no such partition.
  const grandTotal = arches.reduce((sum, a) => sum + a.totalCents, 0);
  // Bars scale against the largest *arche*, excluding the footer bucket.
  const maxTotal = archeRows.length > 0 ? Math.max(...archeRows.map((a) => a.totalCents)) : 0;
  const visible = expanded ? archeRows : archeRows.slice(0, COLLAPSED_LIMIT);

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-sm font-medium text-muted-foreground">Spending by Arche</h2>
        <span className="text-sm text-muted-foreground">
          {formatCreditsWithDollar(grandTotal)} total
        </span>
      </div>

      {archeRows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          None of your spend is tied to a specific arche yet.
        </p>
      ) : (
        <div className="space-y-3">
          {visible.map((row) => {
            const pct = maxTotal > 0 ? (row.totalCents / maxTotal) * 100 : 0;
            return (
              <div key={row.archeId}>
                <div className="flex items-baseline justify-between mb-1 gap-2">
                  <span className="flex items-center gap-2 min-w-0">
                    {row.imageRef ? (
                      <ArcheIconImg
                        imageRef={row.imageRef}
                        size={64}
                        alt=""
                        className="h-5 w-5 rounded-sm shrink-0"
                      />
                    ) : (
                      <span className="h-5 w-5 rounded-sm bg-muted shrink-0" aria-hidden="true" />
                    )}
                    <span className="text-sm font-medium text-foreground truncate">
                      {row.name ?? row.archeId}
                    </span>
                  </span>
                  <div className="flex items-baseline gap-2 shrink-0">
                    <span className="text-xs text-muted-foreground">{row.count} txns</span>
                    <span className="text-sm font-medium text-foreground">
                      {formatCreditsWithDollar(row.totalCents)}
                    </span>
                  </div>
                </div>
                <div className="h-2 rounded-full bg-primary/10">
                  <div
                    className="h-2 rounded-full bg-primary transition-all"
                    style={{ width: `${Math.max(pct, 1)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {archeRows.length > COLLAPSED_LIMIT && (
        <button
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-4 text-sm font-medium text-primary hover:text-primary/80"
        >
          {expanded ? 'Show less' : `Show all ${archeRows.length}`}
        </button>
      )}

      {platformRow && (
        <div className="mt-4 pt-3 border-t border-border">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm font-medium text-muted-foreground">
              {PLATFORM_BUCKET_LABEL}
            </span>
            <div className="flex items-baseline gap-2 shrink-0">
              <span className="text-xs text-muted-foreground">{platformRow.count} txns</span>
              <span className="text-sm font-medium text-foreground">
                {formatCreditsWithDollar(platformRow.totalCents)}
              </span>
            </div>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Plugin builder, platform utilities, and core system features — not billed to a specific
            arche.
          </p>
        </div>
      )}
    </div>
  );
}
